'use client'

/**
 * Push enablement, shared by the toggle and the contextual prompt.
 *
 * WHY THIS EXISTS (owner 2026-08-08: "fix the push permission prompt so people actually grant it").
 * 4 users out of ~900 had granted permission, so the notification system could barely reach anyone.
 * Three causes, and the first is the one that does lasting damage:
 *
 * 🚨 1. THE NATIVE PROMPT IS ONE-SHOT AND PERMANENT. `Notification.requestPermission()` can only be
 *    answered once per origin. If a user clicks Block, the browser NEVER shows that dialog again and
 *    there is no API to re-ask: they have to go into site settings themselves, which nobody does. The
 *    old toggle called requestPermission() directly on click, so an uncertain user who clicked to see
 *    what it was could burn the only chance we get. Everything below exists to protect that one shot.
 *    The fix is the standard soft-ask: show OUR OWN card first, and only call the browser API for
 *    someone who has already said yes to us.
 *
 * 2. IT WAS BURIED. The toggle lived inside the bell dropdown, the onboarding checklist and the price
 *    alerts page. All three require you to already be looking for it.
 *
 * 3. IT ASKED COLD. "Enable alerts" with no reason attached, at no particular moment. People grant
 *    notification permission when they have just done something they want the answer to.
 */

const PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || ''

/** Why we are asking. Drives the copy, so the ask names the thing they just did. */
export type PushReason = 'support' | 'message' | 'price_alert' | 'watchlist' | 'generic' | 'premium'

export const PUSH_PROMPT_EVENT = 'dt:push-prompt'

/** localStorage keys. Versioned so a future policy change can start clean. */
const K_DISMISSED = 'dt_push_prompt_dismissed_v1'   // how many times they said "not now"
const K_LAST_ASK = 'dt_push_prompt_last_v1'         // ms timestamp of the last soft ask
const K_SESSIONS = 'dt_push_sessions_v1'            // how many distinct browsing sessions we have seen
const K_SESSION_MARK = 'dt_push_session_mark_v1'    // sessionStorage: has THIS session been counted
// The iPhone INSTALL card keeps its OWN budget. Declining to install says nothing about wanting notifications,
// and a shared counter would leave someone who dismissed 3 install cards and THEN installed never asked for
// permission at all (deployment-window review 2026-10-18). iOS usually gives Home Screen apps separate storage
// from Safari, which would hide this, but that is not something to depend on.
const K_INSTALL_DISMISSED = 'dt_push_install_dismissed_v1'
const K_INSTALL_LAST = 'dt_push_install_last_v1'

/** Max soft asks before we stop for good, and the minimum gap between them. */
export const MAX_SOFT_ASKS = 3
export const MIN_ASK_GAP_MS = 7 * 24 * 60 * 60 * 1000   // 7 days

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
    && !!PUBLIC_KEY
}

/** 'granted' | 'denied' | 'default' | null when push is unsupported. */
export function pushPermission(): NotificationPermission | null {
  if (!pushSupported()) return null
  return Notification.permission
}

/**
 * May we show the SOFT ask right now?
 *
 * ⚠ Only ever for permission === 'default'. Asking someone who already granted is noise, and asking
 * someone who denied is worse than noise: we cannot re-open the native dialog, so the card would be a
 * button that cannot do what it says.
 */
export function canSoftAsk(): boolean {
  if (!pushSupported()) return false
  if (Notification.permission !== 'default') return false
  return askBudgetOk(K_DISMISSED, K_LAST_ASK)
}

/** A nag budget: at most MAX_SOFT_ASKS, at least MIN_ASK_GAP_MS apart. Each card type passes its own keys. */
function askBudgetOk(dismissedKey: string, lastKey: string): boolean {
  try {
    const dismissed = Number(localStorage.getItem(dismissedKey) || '0')
    if (dismissed >= MAX_SOFT_ASKS) return false
    const last = Number(localStorage.getItem(lastKey) || '0')
    if (last && Date.now() - last < MIN_ASK_GAP_MS) return false
    return true
  } catch {
    return false   // private mode: do not risk nagging on every page load
  }
}

/**
 * 🚨 THE iPHONE ASK WAS A SILENT NO-OP (register iphone-push-ask-was-a-silent-no-op-1018). In an iPhone
 * browser TAB, web push does not exist until the site is added to the Home Screen (Apple rule, iOS 16.4+),
 * so pushSupported() is false, canSoftAsk() is false, and every trigger did nothing at all. Measured
 * 2026-10-18: the permission card reached 175 desktop and 43 Android users but only 4 iPhone users, and
 * those 4 (the ones who had installed it) all granted. iPhone users are the largest mobile group and phone
 * users convert about 2x desktop. So an iPhone tab now gets the INSTALL card instead: same moments, the same
 * limits (3 asks, a week apart) but its OWN counters, so declining to install never uses up the later
 * notification ask. Once opened from the Home Screen the normal permission ask works.
 */
export function isIPhoneTabNotInstalled(): boolean {
  if (typeof window === 'undefined' || platformTag() !== 'ios') return false
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
    || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
  return !standalone
}

/** May we show the iPhone INSTALL card right now? Only in an uninstalled iPhone tab, within the budget. */
export function canInstallAsk(): boolean {
  if (!isIPhoneTabNotInstalled()) return false
  if (pushSupported()) return false   // already able to ask for permission directly: use the normal card
  return askBudgetOk(K_INSTALL_DISMISSED, K_INSTALL_LAST)
}

/** Install-card budget writers: separate keys, so they never spend the notification-permission budget. */
export function recordInstallAskShown(): void {
  try { localStorage.setItem(K_INSTALL_LAST, String(Date.now())) } catch { /* private mode */ }
}
export function recordInstallAskDismissed(): void {
  try {
    localStorage.setItem(K_INSTALL_DISMISSED, String(Number(localStorage.getItem(K_INSTALL_DISMISSED) || '0') + 1))
    localStorage.setItem(K_INSTALL_LAST, String(Date.now()))
  } catch { /* private mode */ }
}

export function recordSoftAskShown(): void {
  try { localStorage.setItem(K_LAST_ASK, String(Date.now())) } catch { /* private mode */ }
}

export function recordSoftAskDismissed(): void {
  try {
    localStorage.setItem(K_DISMISSED, String(Number(localStorage.getItem(K_DISMISSED) || '0') + 1))
    localStorage.setItem(K_LAST_ASK, String(Date.now()))
  } catch { /* private mode */ }
}

/** Stop asking forever once they have granted, so a cleared permission does not restart the cycle. */
export function recordGranted(): void {
  try { localStorage.setItem(K_DISMISSED, String(MAX_SOFT_ASKS)) } catch { /* private mode */ }
}

/**
 * Ask for the soft prompt from anywhere, without threading props through the tree.
 * A no-op unless we are actually allowed to ask, so callers can fire it optimistically after an
 * action without checking anything themselves.
 */
export function requestPushPrompt(reason: PushReason): void {
  if (!canSoftAsk() && !canInstallAsk()) return
  try { window.dispatchEvent(new CustomEvent(PUSH_PROMPT_EVENT, { detail: { reason } })) } catch { /* ignore */ }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

/**
 * Store a subscription server-side, healing a DEAD endpoint.
 *
 * A plain re-POST upserts and returns ok. But if the server answers 409, this endpoint already
 * returned 410/404 on a prior send (webPush.ts recorded it in push_dead_endpoints) and the browser is
 * handing us that same retired endpoint from getSubscription(). Re-POSTing it would resurrect a row the
 * next send prunes again - the prune/re-POST loop in push-subscriptions-silently-rot-no-reheal-0922
 * that leaves a user delivered-to with zero live subscription. So on 409 we drop the stale local sub
 * with unsubscribe() and mint a genuinely fresh endpoint, then POST that ONCE (no recursion, so a push
 * service that somehow re-issued the same endpoint cannot loop). Permission is already granted, so
 * minting needs no new prompt. Any non-409 failure (401 signed-out, 500) is left alone - it is not a
 * dead endpoint and re-minting would not help.
 */
async function storeSubscription(reg: ServiceWorkerRegistration, sub: PushSubscription): Promise<boolean> {
  const post = (s: PushSubscription) => fetch('/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(s),
  })
  const res = await post(sub)
  if (res.ok) return true
  if (res.status !== 409) return false
  try { await sub.unsubscribe() } catch { /* ignore */ }
  const fresh = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
  })
  return (await post(fresh)).ok
}

export type EnableResult = 'granted' | 'denied' | 'failed' | 'unsupported'

/**
 * WHY `failed` NEEDED SPLITTING (measured 2026-08-16). push_prompt_events held FIVE 'failed' rows
 * against two grants, all Android, and the register's own reading rule says failed>0 means OUR BUG.
 * It sent me hunting one. But three unrelated things all returned 'failed' from one function:
 *
 *   1. Notification.requestPermission() resolved 'default' - the user closed the native dialog
 *      without choosing. THAT IS NOT A FAILURE, it is the same act as dismissing our own card, and
 *      on Android it is the most common outcome of all. Counting it as a bug is what made the
 *      numbers unreadable.
 *   2. POST /api/push/subscribe returned non-OK. A real server-side failure.
 *   3. Anything threw - service worker registration, pushManager.subscribe, the network.
 *
 * Only 2 and 3 are ours to fix, so only they report as failures now. `cause` names which, and it
 * rides on the EVENT (a text column) rather than a new column, so this needed no migration.
 */
export type EnableCause = 'native_dismissed' | 'subscribe_rejected' | 'exception'

/**
 * Register the service worker, request permission and store the subscription server-side.
 *
 * ⚠ MUST BE CALLED FROM A USER GESTURE. Browsers reject requestPermission() outside one, and Chrome
 * additionally ignores it without a prior interaction. Every caller is a click handler.
 */
export async function enablePush(onCause?: (cause: EnableCause) => void): Promise<EnableResult> {
  if (!pushSupported()) return 'unsupported'
  try {
    const reg = await navigator.serviceWorker.register('/sw.js')
    await navigator.serviceWorker.ready
    const perm = await Notification.requestPermission()
    if (perm === 'denied') return 'denied'
    if (perm !== 'granted') {
      // 'default': the native dialog was closed without an answer. Our one shot is NOT spent, so
      // this is a dismissal and must never be reported as a failure. See EnableCause above.
      onCause?.('native_dismissed')
      return 'failed'
    }

    const existing = await reg.pushManager.getSubscription()
    const sub = existing ?? await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
    })
    const stored = await storeSubscription(reg, sub)
    if (!stored) {
      // ⚠ THE WORST CASE IN THE WHOLE FLOW: permission was GRANTED and we failed to store it. The
      // one-shot native dialog is spent and the user has no subscription, so they look identical to
      // someone who never granted and we can never re-ask. Always a real bug.
      onCause?.('subscribe_rejected')
      return 'failed'
    }
    recordGranted()
    return 'granted'
  } catch {
    onCause?.('exception')
    return 'failed'
  }
}

/**
 * SELF-HEAL the SERVER-side push subscription on load.
 *
 * 🚨 WHY (register: push-subscription-desync-ui-says-on-server-empty-0916). enablePush creates the LOCAL
 * browser subscription and then POSTs it to /api/push/subscribe. If that POST fails - a 401 in the
 * installed-PWA context (a separate push context from the Chrome tab), or a transient 500 - the local
 * subscription is left in place, so the toggle reads "Alerts on" (it checks the local sub, not a server
 * row) while the server has NOTHING to deliver to. That is exactly the state a paying trial user hit:
 * in-app bell rows appear, the phone never buzzes, and it never self-corrects.
 *
 * So on every authenticated load, with permission already 'granted': if this browser HAS a local
 * subscription, re-POST it (the endpoint upserts ON CONFLICT, so it is idempotent and cheap, and it
 * re-registers a subscription the server dropped or never stored). If the browser has NO local
 * subscription - the endpoint was rotated/retired, the same 410 that pruned it server-side - mint a
 * fresh one with subscribe() and POST that. Because permission is already granted, minting needs NO
 * new prompt, so this heals silent rot (push-subscriptions-silently-rot-no-reheal-0922) without ever
 * re-opening the one-shot native dialog. Best-effort and silent: it never throws, and it does nothing
 * when permission is not granted or the user is signed out (the POST 401s harmlessly). It still is NOT
 * the ask - a user who has not granted is only reached by the deliberate enablePush path.
 */
export async function rehealPushSubscription(): Promise<boolean> {
  try {
    if (!pushSupported() || pushPermission() !== 'granted') return false
    const reg = (await navigator.serviceWorker.getRegistration())
      ?? (await navigator.serviceWorker.register('/sw.js'))
    await navigator.serviceWorker.ready
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      // ROTATED / NEVER MINTED ON THIS BROWSER. The push service can retire an endpoint (the same
      // 410 that made webPush.ts prune the row server-side), leaving getSubscription() null while
      // Notification.permission stays 'granted'. The old resubscribeIfLocal gave up here, which is
      // the exact silent rot in push-subscriptions-silently-rot-no-reheal-0922 (a user with 416
      // delivered pushes, then a 410, then 0 subscriptions and no way back short of re-toggling).
      // Because permission is ALREADY granted, subscribe() mints a fresh endpoint with NO new prompt.
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
      })
    }
    // storeSubscription heals the case this entry is about: if the endpoint handed back by
    // getSubscription() is one the server already pruned on a 410, the re-POST gets a 409 and we
    // unsubscribe()+re-mint a fresh endpoint rather than resurrecting the dead one.
    return await storeSubscription(reg, sub)
  } catch {
    return false
  }
}


/**
 * Count distinct browsing sessions, once each.
 *
 * ⚠ sessionStorage IS THE SESSION BOUNDARY, localStorage IS THE TALLY. Using localStorage alone would
 * count every page navigation, so a single visit would look like twenty sessions and the ambient ask
 * would fire on someone's first minute, which is precisely the cold ask that was converting at 0.4%.
 */
export function noteSessionAndCount(): number {
  try {
    if (!sessionStorage.getItem(K_SESSION_MARK)) {
      sessionStorage.setItem(K_SESSION_MARK, '1')
      const n = Number(localStorage.getItem(K_SESSIONS) || '0') + 1
      localStorage.setItem(K_SESSIONS, String(n))
      return n
    }
    return Number(localStorage.getItem(K_SESSIONS) || '0')
  } catch {
    return 0   // private mode: never eligible for the ambient ask
  }
}

/**
 * Sessions before the AMBIENT ask is allowed. The contextual triggers (support, DM, price alert,
 * watchlist) all fire on a deliberate action; this one has no action behind it, so it has to earn the
 * right to appear some other way. Coming back three times is the weakest honest signal of intent.
 */
export const AMBIENT_MIN_SESSIONS = 3


/**
 * The funnel stages, in order. Mirrors the server's allow-list.
 *
 * ⚠ `failed` IS KEPT ONLY FOR THE ROWS ALREADY IN THE TABLE. New code emits one of the three
 * specific variants instead, because a bare 'failed' count cannot be acted on - see EnableCause.
 * Of the three, only failed_subscribe and failed_exception are bugs; failed_native_dismissed is a
 * user closing the browser's own dialog and should be read alongside 'dismissed'.
 */
export type PromptEvent =
  | 'shown' | 'granted' | 'dismissed' | 'denied' | 'failed'
  | 'failed_native_dismissed' | 'failed_subscribe' | 'failed_exception'
  | 'install_shown' | 'install_dismissed'

/** EnableCause -> the funnel event that records it. */
export const CAUSE_EVENT: Record<EnableCause, PromptEvent> = {
  native_dismissed: 'failed_native_dismissed',
  subscribe_rejected: 'failed_subscribe',
  exception: 'failed_exception',
}

/**
 * iOS is the single biggest confound in web-push adoption: it requires the site to be INSTALLED to the
 * home screen, so a poor grant rate on iOS says nothing about the copy or the moment. Recorded with
 * every event so the funnel can be read per platform rather than as one misleading average.
 */
export function platformTag(): 'ios' | 'android' | 'desktop' | 'unknown' {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent || ''
  // iPadOS 13+ reports as a Mac, so the touch-point check is what actually catches modern iPads.
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
    || (/Macintosh/i.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document)
  if (isIOS) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

/**
 * Record one funnel step. Fire-and-forget by design: telemetry must never block, slow or break the
 * thing it is measuring, so every failure path is swallowed.
 *
 * `keepalive` matters because 'dismissed' and 'granted' can be the last thing that happens before a
 * navigation, and a plain fetch would be cancelled mid-flight, quietly under-counting the two events
 * the whole funnel exists to compare.
 */
export function recordPromptEvent(event: PromptEvent, reason: PushReason): void {
  try {
    fetch('/api/push/prompt-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, reason, platform: platformTag() }),
      keepalive: true,
    }).catch(() => {})
  } catch { /* never let telemetry surface */ }
}
